import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';

export type InteractionEntity = 'contact' | 'organization' | 'opportunity';

export type Interaction = {
  id?: string;
  entity_id: string;
  entity_type: InteractionEntity;
  interaction_date?: string;
  interaction_type: string;
  sentiment?: string;
  notes?: string;
  user_id?: string;
  created_at?: string;
  updated_at?: string;
};

// Create service object
export const interactionsService = {
  getInteractionsByEntity: async (entityType: InteractionEntity, entityId: string) => {
    try {
      // Try with relationship first
      const { data, error } = await supabase
        .from('interactions')
        .select(
          `
          *,
          user:user_profiles!interactions_user_id_fkey (
            id, 
            first_name, 
            last_name, 
            avatar_url
          )
        `
        )
        .eq('entity_id', entityId)
        .eq('entity_type', entityType)
        .order('interaction_date', { ascending: false });

      if (error) {
        // Fallback to simple query without relationship
        const { data: simpleData, error: simpleError } = await supabase
          .from('interactions')
          .select('*')
          .eq('entity_id', entityId)
          .eq('entity_type', entityType)
          .order('interaction_date', { ascending: false });

        if (simpleError) {
          throw simpleError;
        }

        return simpleData || [];
      }

      return data || [];
    } catch (error) {
      logError('Error fetching interactions:', error);
      // Return empty array on error to prevent UI crashes
      return [];
    }
  },

  createInteraction: async (interactionData: Interaction) => {
    const { data, error } = await supabase
      .from('interactions')
      .insert({
        ...interactionData,
        interaction_date: interactionData.interaction_date || new Date().toISOString(),
      })
      .select('*')
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data;
  },

  updateInteraction: async (id: string, interactionData: Partial<Interaction>) => {
    const { data, error } = await supabase
      .from('interactions')
      .update(interactionData)
      .eq('id', id)
      .select('*')
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data;
  },

  deleteInteraction: async (id: string) => {
    const { error } = await supabase.from('interactions').delete().eq('id', id);

    if (error) {
      throw error;
    }

    return { success: true, id };
  },
};

// Legacy export functions for backward compatibility
export const fetchInteractions = (entityId: string, entityType: InteractionEntity) =>
  interactionsService.getInteractionsByEntity(entityType, entityId);
export const createInteraction = interactionsService.createInteraction;
export const updateInteraction = interactionsService.updateInteraction;
export const deleteInteraction = interactionsService.deleteInteraction;

export const fetchContactInteractions = async (contactId: string) => {
  return interactionsService.getInteractionsByEntity('contact', contactId);
};

export const createContactInteraction = async (data: any) => {
  const interactionData: Interaction = {
    entity_id: data.contact_id,
    entity_type: 'contact',
    interaction_type: data.interaction_type,
    sentiment: data.sentiment,
    notes: data.notes,
    user_id: data.user_id,
  };

  return interactionsService.createInteraction(interactionData);
};

export const fetchOrganizationInteractions = async (organizationId: string) => {
  return interactionsService.getInteractionsByEntity('organization', organizationId);
};

export const createOrganizationInteraction = async (data: any) => {
  const interactionData: Interaction = {
    entity_id: data.organization_id,
    entity_type: 'organization',
    interaction_type: data.interaction_type,
    sentiment: data.sentiment,
    notes: data.notes,
    user_id: data.user_id,
  };

  return interactionsService.createInteraction(interactionData);
};

export const fetchOpportunityInteractions = async (opportunityId: string) => {
  return interactionsService.getInteractionsByEntity('opportunity', opportunityId);
};

export const createOpportunityInteraction = async (data: any) => {
  const interactionData: Interaction = {
    entity_id: data.opportunity_id,
    entity_type: 'opportunity',
    interaction_type: data.interaction_type,
    sentiment: data.sentiment,
    notes: data.notes,
    user_id: data.user_id,
  };

  return interactionsService.createInteraction(interactionData);
};
